// Ported from dev-scrips-pm-react src/services/scheduling/use-appointment-profiles.ts (PROD-1190 calendar port)
import { useQuery } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import type {
  CalendarSlotsRequest,
  CalendarSlotsResponse,
  CreateAppointmentProfileRequest,
} from '@/types/scheduling-api';
import { dedupeProfilesPreferRoom } from '@/scheduling/lib/quick-create-slot';

interface AppointmentProfilesParams {
  organizationId: string;
  practiceId: string;
  providerId: string;
}

/**
 * Appointment profiles for the create-panel profile picker.
 *
 * SOURCE: `POST /api/v1/Practice/calendarSlots` (the scheduling feed),
 * matching Flutter's scheduling create flow — NOT `/api/Doctor/AppointmentProfileList`
 * (that's the Settings-module endpoint; Flutter only uses it under
 * `settings_module`). Why this matters (room-mitigation, 2026-06-03):
 *
 *   A provider can carry DUPLICATE profiles of the same name where one has a
 *   usable room and the other does not. `AppointmentProfileList` for such a
 *   provider returned ONLY the room-less duplicate (e.g. "First Appointment -
 *   Virtual" `bc0d4b0d` → dead room) — so the create panel could only book the
 *   broken one. `calendarSlots` carries the live duplicate too (`0ab0e307` →
 *   real room). Booking the room-less one yields an appointment the backend
 *   can't load/reschedule (`GetRoomByIdAsync` 500s/empties — reproduces in
 *   Flutter as well). So we source from `calendarSlots` and collapse same-name
 *   duplicates to the room-bearing one (`dedupePreferRoom`).
 *
 * Profiles are provider config (not slot-dependent), so any day works for the
 * range; we pass today.
 */

/** Local YYYY-MM-DD (avoids the toISOString UTC day-shift). */
function todayLocalISODate(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function useAppointmentProfiles(params: AppointmentProfilesParams | null) {
  return useQuery({
    queryKey: ['appointment-profiles', params],
    queryFn: async ({ signal }): Promise<CreateAppointmentProfileRequest[]> => {
      if (!params) throw new Error('useAppointmentProfiles called without params');
      const day = todayLocalISODate();
      const body: CalendarSlotsRequest = {
        organizationId: params.organizationId,
        practiceId: params.practiceId,
        startDate: `${day}T00:00:00Z`,
        endDate: `${day}T23:59:59Z`,
        practitioners: [params.providerId],
        searchText: '',
        pageNumber: 0,
        pageSize: 50,
      } as CalendarSlotsRequest;
      const res = await post<CalendarSlotsResponse>(
        '/api/v1/Practice/calendarSlots',
        body,
        { signal },
      );
      const provider = (res ?? []).find((p) => p.id === params.providerId);
      return dedupeProfilesPreferRoom(provider?.appointmentProfile ?? []);
    },
    enabled: !!params?.providerId && !!params?.practiceId,
  });
}
